// Hook for the GET /users/me endpoint. Backs the gamification surfaces
// (profile, library header, video detail). In mock-auth mode there is no
// backend, so the hook returns a benign synthetic profile so the UI still
// renders during local dev.

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { isMockAuth } from "../auth/config";
import {
  CurrentUserProfile,
  getCurrentUser,
  isApiConfigured,
} from "./api-client";

const MOCK_PROFILE: CurrentUserProfile = {
  user: {
    userId: "mock-user-1",
    email: "mock@example.com",
    givenName: "Mock",
    familyName: "User",
    displayName: "Mock User",
    createdAt: null,
  },
  stats: {
    xp: 0,
    xpPerObjective: 10,
    xpPerVideoComplete: 50,
    xpPerFirstActionOfDay: 20,
    xpPerBookmark: 2,
    xpBreakdown: {
      objectives: 0,
      videosComplete: 0,
      firstOfDayBonuses: 0,
      bookmarks: 0,
    },
    completedObjectives: 0,
    completedVideos: 0,
    currentStreak: 0,
    longestStreak: 0,
    lastPracticeDate: null,
    practiceTimeZone: "America/Denver",
    weeklyStreak: 0,
    longestWeeklyStreak: 0,
    currentWeekPracticeDays: [],
    weekTarget: 4,
    weeklyRecap: {
      objectivesCheckedThisWeek: 0,
      videosCompletedThisWeek: 0,
      firstOfDayThisWeek: 0,
      bookmarksThisWeek: 0,
      bookmarkXpThisWeek: 0,
      xpThisWeek: 0,
    },
  },
};

export interface UseCurrentUserState {
  profile: CurrentUserProfile | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const API_AVAILABLE = isApiConfigured && !isMockAuth;

export function useCurrentUser(): UseCurrentUserState {
  const { user, getIdToken } = useAuth();
  const [profile, setProfile] = useState<CurrentUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }
    if (!API_AVAILABLE) {
      setProfile({
        ...MOCK_PROFILE,
        user: {
          ...MOCK_PROFILE.user,
          userId: user.sub,
          email: user.email ?? null,
          givenName: user.givenName ?? null,
          familyName: user.familyName ?? null,
          displayName:
            user.givenName ?? user.email ?? MOCK_PROFILE.user.displayName,
        },
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error("No auth token available.");
      const data = await getCurrentUser(token);
      setProfile(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load profile.");
    } finally {
      setLoading(false);
    }
  }, [user, getIdToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { profile, loading, error, refresh };
}
